import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type {
  ExcelCheckPkDuplicatesResponse,
  ExcelColumnType,
  ExcelDateFormat,
  ExcelParseSheet,
  ExcelSheetCommit,
} from '@innermate/shared';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';

const COLUMN_TYPE_LABELS: Record<ExcelColumnType, string> = {
  text: '문자열 (text)',
  numeric: '숫자 (numeric)',
  timestamptz: '날짜/시각 (timestamptz)',
  boolean: '참/거짓 (boolean)',
};

const DATE_FORMAT_LABELS: Record<ExcelDateFormat, string> = {
  YYYYMMDD: 'YYYYMMDD (예: 20260102)',
  'YYYY-MM-DD': 'YYYY-MM-DD (일 단위 교체)',
  'YYYY-MM': 'YYYY-MM (월 단위 교체)',
};

interface Props {
  sheet: ExcelParseSheet;
  state: ExcelSheetCommit;
  onChange: (next: ExcelSheetCommit) => void;
  /** PK 중복 검사용 fileUploadId. 없으면 검사 버튼 미노출. */
  fileUploadId?: string;
}

export function ExcelSheetReviewPanel({ sheet, state, onChange, fileUploadId }: Props) {
  const update = (patch: Partial<ExcelSheetCommit>) => onChange({ ...state, ...patch });
  const updateColumn = (idx: number, patch: Partial<ExcelSheetCommit['columns'][number]>) => {
    const next = state.columns.slice();
    const cur = next[idx];
    if (!cur) return;
    next[idx] = { ...cur, ...patch };
    onChange({ ...state, columns: next });
  };
  const togglePk = (sqlName: string) => {
    const has = state.pkColumns.includes(sqlName);
    update({
      pkColumns: has ? state.pkColumns.filter((c) => c !== sqlName) : [...state.pkColumns, sqlName],
    });
  };
  const setDateColumn = (sqlName: string | null) => {
    update({
      dateColumn: sqlName,
      dateFormat: sqlName ? (state.dateFormat ?? 'YYYY-MM-DD') : null,
    });
  };

  const includedColumns = state.columns.filter((c) => c.include !== false);

  return (
    <section className="space-y-4 rounded-2xl border border-border bg-surface px-4 py-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium">시트: {sheet.sheetName}</h3>
          <p className="mt-0.5 text-xs text-foreground-muted">
            {sheet.rowCount.toLocaleString()} 행 · {sheet.columns.length} 컬럼 (포함{' '}
            {includedColumns.length})
            {sheet.headerRowIndex !== null ? ` · 헤더행 ${sheet.headerRowIndex}` : ''}
            {sheet.isData ? '' : ' · 데이터 시트가 아님 (자동 비활성)'}
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={state.include}
            onChange={(e) => update({ include: e.target.checked })}
          />
          시트 포함
        </label>
      </header>

      {state.include ? (
        <>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-muted w-24 shrink-0">SQL 테이블명</span>
            <Input
              value={state.tableName}
              onChange={(e) => update({ tableName: e.target.value })}
              className="font-mono text-xs h-8"
            />
          </div>

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <thead className="bg-surface-muted/40 text-[10px] uppercase tracking-wider text-foreground-muted">
                <tr>
                  <Th align="center" width="w-12">
                    포함
                  </Th>
                  <Th>SQL 컬럼명</Th>
                  <Th>한글 라벨</Th>
                  <Th>설명 (옵션)</Th>
                  <Th>타입</Th>
                  <Th align="center">PK</Th>
                  <Th align="center">날짜키</Th>
                </tr>
              </thead>
              <tbody>
                {state.columns.map((col, idx) => {
                  const orig = sheet.columns[idx];
                  const headerMissing = orig?.rawHeader === null || orig?.rawHeader === undefined;
                  const detectedEmpty = orig?.detectedType === 'empty';
                  const isIncluded = col.include !== false;
                  return (
                    <tr
                      key={col.sqlName + idx}
                      className={cn(
                        'border-t border-border align-top',
                        !isIncluded && 'opacity-40',
                      )}
                    >
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={isIncluded}
                          onChange={(e) => updateColumn(idx, { include: e.target.checked })}
                          aria-label="컬럼 포함"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono">
                        <Input
                          value={col.sqlName}
                          onChange={(e) => updateColumn(idx, { sqlName: e.target.value })}
                          disabled={!isIncluded}
                          className="font-mono text-xs h-7"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={col.label}
                          onChange={(e) => updateColumn(idx, { label: e.target.value })}
                          placeholder={headerMissing ? '필수: 한글 라벨 입력' : ''}
                          disabled={!isIncluded}
                          className={cn(
                            'h-7 text-xs',
                            isIncluded && headerMissing && col.label.trim().length === 0
                              ? 'border-destructive bg-destructive/5'
                              : '',
                          )}
                        />
                        {detectedEmpty ? (
                          <p className="mt-0.5 text-[10px] text-amber-600">
                            데이터 없음 — 제거 권장
                          </p>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={col.description ?? ''}
                          onChange={(e) =>
                            updateColumn(idx, {
                              description: e.target.value === '' ? null : e.target.value,
                            })
                          }
                          disabled={!isIncluded}
                          className="h-7 text-xs"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={col.type}
                          onChange={(e) =>
                            updateColumn(idx, { type: e.target.value as ExcelColumnType })
                          }
                          disabled={!isIncluded}
                          className="h-7 rounded-md border border-border bg-surface px-2 text-xs disabled:opacity-50"
                        >
                          {(Object.keys(COLUMN_TYPE_LABELS) as ExcelColumnType[]).map((t) => (
                            <option key={t} value={t}>
                              {COLUMN_TYPE_LABELS[t]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={state.pkColumns.includes(col.sqlName)}
                          disabled={!isIncluded}
                          onChange={() => togglePk(col.sqlName)}
                        />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input
                          type="radio"
                          name={`date-${state.tableName}`}
                          checked={state.dateColumn === col.sqlName}
                          disabled={
                            !isIncluded || (col.type !== 'timestamptz' && col.type !== 'text')
                          }
                          onChange={() => setDateColumn(col.sqlName)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {state.dateColumn ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-muted w-24 shrink-0">날짜 형식</span>
              <select
                value={state.dateFormat ?? 'YYYY-MM-DD'}
                onChange={(e) => update({ dateFormat: e.target.value as ExcelDateFormat })}
                className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
              >
                {(Object.keys(DATE_FORMAT_LABELS) as ExcelDateFormat[]).map((f) => (
                  <option key={f} value={f}>
                    {DATE_FORMAT_LABELS[f]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="text-xs text-foreground-muted underline"
                onClick={() => setDateColumn(null)}
              >
                날짜키 해제
              </button>
            </div>
          ) : null}

          <StrategyHint pkCount={state.pkColumns.length} hasDate={!!state.dateColumn} />

          {/* PK 중복 검사 */}
          {state.pkColumns.length > 0 && fileUploadId ? (
            <PkDuplicateCheck
              fileUploadId={fileUploadId}
              sheetName={state.sheetName}
              pkSqlNames={state.pkColumns}
              columns={state.columns}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function StrategyHint({ pkCount, hasDate }: { pkCount: number; hasDate: boolean }) {
  if (pkCount > 0) {
    return (
      <p className="text-xs text-success">
        ✓ PK {pkCount} 개 — 추가 업로드 시 PK 일치 행은 update, 신규는 insert (upsert).
      </p>
    );
  }
  if (hasDate) {
    return (
      <p className="text-xs text-foreground-muted">
        날짜키 모드 — 추가 업로드 시 새 데이터의 날짜에 해당하는 기존 데이터는 삭제 후 새로 적재.
      </p>
    );
  }
  return (
    <p className="text-xs text-amber-600">
      ⚠ PK / 날짜키 모두 없음 — 추가 업로드 시 단순 append (중복 발생 가능). 필요하면 위에서 PK 또는
      날짜키를 지정하세요.
    </p>
  );
}

function PkDuplicateCheck({
  fileUploadId,
  sheetName,
  pkSqlNames,
  columns,
}: {
  fileUploadId: string;
  sheetName: string;
  pkSqlNames: string[];
  columns: ExcelSheetCommit['columns'];
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ExcelCheckPkDuplicatesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 영구 sqlName → 임시 parseSqlName 매핑 (PK 검사는 임시 테이블 컬럼명 사용).
  const pkParseColumns = pkSqlNames
    .map((n) => columns.find((c) => c.sqlName === n)?.parseSqlName)
    .filter((v): v is string => typeof v === 'string');

  const onCheck = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.dataSourceExcelCheckPkDuplicates({
        fileUploadId,
        sheetName,
        pkParseColumns,
      });
      setResult(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-surface-muted/20 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-foreground-muted">
          PK 중복 검사 — 등록 전 임시 데이터에서 PK 컬럼 중복 여부 확인
        </span>
        <Button variant="secondary" size="sm" onClick={onCheck} disabled={loading}>
          {loading ? <Loader2 className="size-3.5 animate-spin" /> : '검사 실행'}
        </Button>
      </div>
      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : result ? (
        result.hasDuplicates ? (
          <div className="space-y-1.5">
            <p className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <XCircle className="size-3.5" />
              중복 PK {result.totalDuplicateGroups.toLocaleString()} 그룹 발견 — PK 사용 불가
            </p>
            <div className="overflow-x-auto rounded border border-border bg-surface">
              <table className="w-full text-[11px]">
                <thead className="bg-surface-muted/40 text-foreground-muted">
                  <tr>
                    {pkSqlNames.map((n) => (
                      <th key={n} className="px-2 py-1 text-left font-medium">
                        {n}
                      </th>
                    ))}
                    <th className="px-2 py-1 text-right font-medium">중복</th>
                  </tr>
                </thead>
                <tbody>
                  {result.samples.map((s, i) => (
                    <tr key={i} className="border-t border-border">
                      {s.values.map((v, j) => (
                        <td key={j} className="px-2 py-1 font-mono">
                          {v ?? <span className="text-foreground-muted">NULL</span>}
                        </td>
                      ))}
                      <td className="px-2 py-1 text-right tabular-nums">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="flex items-center gap-1.5 text-xs text-success">
            <CheckCircle2 className="size-3.5" />
            중복 없음 — PK 사용 가능
          </p>
        )
      ) : null}
    </div>
  );
}

function Th({
  children,
  align = 'left',
  width,
}: {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        'px-3 py-2 font-medium',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        width,
      )}
    >
      {children}
    </th>
  );
}
